export interface TestResult {
  test_id: string
  status: string
  ordered_at: string
  orderer: string
  product: string
  variant: string
  lot: string
  assay: string
  analyte: string
  unit: string
  loq: number | null
  lod: number | null
  concentration: number | null
  converted_concentration_ug_kg: number | null
  notes: string | null
}

export interface FormulaTarget {
  product: string
  category: string
  nutrient: string
  targetValue: number
  unit: string
}

export interface RegulatoryThreshold {
  id: number
  analyte: string
  threshold_value: number
  threshold_type: 'concentration' | 'total_daily_intake'
  category: 'regulatory' | 'other'
  unit: string
  guideline_name: string
  reference_url: string | null
  notes: string | null
  converted_value_ug_kg: number | null
  created_at: string
  updated_at: string
}

export interface HeatmapCell {
  product: string
  analyte: string
  concentration: number | null
  loq: number | null
  lod: number | null
  unit: string
  status: 'ND' | 'trace' | 'detected' | 'high'
  colorIntensity: number
  isRollup?: boolean
  rollupMaxPercentage?: number  // For rollup cells: max percentage of limit from nested cells
  rollupDetails?: {
    detectedCount: number
    totalAnalytes: number
    topContributors: Array<{ analyte: string; concentration: number; unit: string }>
  }
}

export interface HeatmapData {
  products: string[]
  analytes: string[]
  cells: Map<string, HeatmapCell>
}

export interface TestCategory {
  name: string
  analytes: string[]
  positiveScale?: {
    targetValue: number // e.g., 5000 mg for 5g of creatine
    unit?: string
    thresholds: {
      excellent: number  // >= this % is green (e.g., 90)
      good: number       // >= this % is trace/yellow (e.g., 60)
      fair: number       // >= this % is detected/orange (e.g., 40)
      // < fair % is high/red
    }
  }
}

export interface RegulatoryGuideline {
  id: string
  name: string
  limits: Record<string, number> // analyte name -> limit value in appropriate unit
  description?: string
}

export const REGULATORY_GUIDELINES: RegulatoryGuideline[] = []

export const TEST_CATEGORIES: TestCategory[] = [
  {
    name: 'Creatine',
    analytes: ['Creatine', 'Creatine Monohydrate', 'Creatine Nitrate', 'Creatinine', 'Dicyandiamide'],
    // TODO: Make target value configurable per product (different products may advertise different amounts)
    positiveScale: {
      targetValue: 5000, // 5g = 5000mg
      unit: 'mg',
      thresholds: {
        excellent: 90,  // >= 90% is green
        good: 60,       // >= 60% is yellow/trace
        fair: 40        // >= 40% is orange/detected, < 40% is red/high
      }
    }
  },
  {
    name: 'Macronutrients',
    analytes: ['Calories', 'Carb', 'Fat', 'Protein'],
    // TODO: Make target values configurable per product and per analyte
    positiveScale: {
      targetValue: 100, // Placeholder - should be product/analyte specific
      unit: 'g',
      thresholds: {
        excellent: 90,  // >= 90% is green
        good: 60,       // >= 60% is yellow/trace
        fair: 40        // >= 40% is orange/detected, < 40% is red/high
      }
    }
  },
  {
    name: 'Minerals',
    analytes: ['Calcium', 'Iron', 'Potassium', 'Sodium'],
    // TODO: Make target values configurable per product and per analyte
    positiveScale: {
      targetValue: 100, // Placeholder - should be product/analyte specific
      unit: 'mg',
      thresholds: {
        excellent: 90,  // >= 90% is green
        good: 60,       // >= 60% is yellow/trace
        fair: 40        // >= 40% is orange/detected, < 40% is red/high
      }
    }
  },
  {
    name: 'Heavy Metals',
    analytes: ['Arsenic', 'Cadmium', 'Lead', 'Mercury']
  },
  {
    name: 'Bisphenols',
    analytes: ['Bisphenol A', 'Bisphenol F', 'Bisphenol S']
  },
  {
    name: 'Herbicides',
    analytes: ['AMPA', 'Glyphosate']
  },
  {
    name: 'Pesticides',
    analytes: [
      'Acephate', 'Acetamiprid', 'Acetochlor', 'Aldicarb', 'Aldicarb Sulfone', 'Aldicarb Sulfoxide',
      'Allethrin', 'Atrazine', 'Azinphos Methyl', 'Azoxystrobin', 'Bendiocarb', 'BHC-Alpha',
      'Bifenazate', 'Bifenthrin', 'Bitertanol', 'Boscalid', 'Bromacil', 'Buprofezin', 'Captan',
      'Carbaryl', 'Carbendazim (MBC)', 'Carbofuran', 'Chlorantraniliprole', 'Chlordane, cis-',
      'Chlordane, trans-', 'Chlorfenapyr', 'Chlorothalonil', 'Chlorpropham', 'Chlorpyrifos',
      'Chlorpyrifos Methyl', 'Clofentezine', 'Clothianidin', 'Coumaphos', 'Cyazofamid', 'Cycloate',
      'Cyfluthrin', 'Cyhalothrin, L-', 'Cypermethrin', 'Cyprodinil', 'Cyromazine', 'DCPA',
      "DDD o,p'", "DDD p,p'", "DDE o,p'", "DDE p,p'", "DDT o,p'", "DDT p,p'", 'DEF (Tribufos)',
      'Deltamethrin (includes Tralomethrin)', 'Diazinon', 'Diazinon Oxygen Analog', 'Dichlorvos (DDVP)',
      'Dicloran', "Dicofol o,p'", "Dicofol p,p' (Kelthane)", 'Dieldrin', 'Difenoconazole',
      'Diflubenzuron', 'Dimethoate', 'Diphenamid', 'Diphenylamine (DPA)', 'Disulfoton',
      'Disulfoton Sulfone', 'Diuron', 'Endosulfan I', 'Endosulfan II', 'Endosulfan Sulfate',
      'Endrin', 'Esfenvalerate', 'Ethion', 'Ethoprop', 'Ethoxyquin', 'Etoxazole', 'Famoxadone',
      'Fenamidone', 'Fenamiphos', 'Fenamiphos Sulfone', 'Fenarimol', 'Fenbuconazole', 'Fenhexamid',
      'Fenpropathrin', 'Fenpyroximate', 'Fenthion', 'Fenvalerate', 'Fipronil', 'Flonicamid',
      'Fludioxonil', 'Fluoxastrobin', 'Fluridone', 'Flutolanil', 'Fluvalinate', 'Folpet', 'Fonofos',
      'Formetanate Hydrochloride', 'Heptachlor Epoxide', 'Hexachlorobenzene (HCB)', 'Hexaconazole',
      'Hexythiazox', 'Hydroxycarbofuran, 3-', 'Imazalil', 'Imidacloprid', 'Indoxacarb', 'Iprodione',
      'Lindane (BHC-gamma)', 'Linuron', 'Malathion', 'Malathion Oxygen Analog', 'Metalaxyl',
      'Methamidophos', 'Methidathion', 'Methiocarb', 'Methomyl', 'Methoxychlor Total', 'Methoxyfenozide',
      'Metolachlor', 'Metribuzin', 'Mevinphos Total', 'MGK-264', 'Myclobutanil', 'Naled',
      'Naphthol, 1-', 'Napropamide', 'Nonachlor, cis-', 'Nonachlor, trans-', 'Omethoate', 'Oxadixyl',
      'Oxamyl', 'Parathion Methyl', 'Pendimethalin', 'Pentachloroaniline (PCA)', 'Pentachlorobenzene (PCB)',
      'Permethrin Total', 'Phenylphenol, O-', 'Phorate Sulfone', 'Phorate Sulfoxide', 'Phosalone',
      'Phosmet', 'Piperonyl Butoxide', 'Pirimiphos Methyl', 'Pirimicarb', 'Prochloraz', 'Procymidone',
      'Prometryn', 'Pronamide', 'Propargite', 'Propiconazole', 'Pymetrozine', 'Pyraclostrobin',
      'Pyridaben', 'Pyrimethanil', 'Pyriproxyfen', 'Quinoxyfen', 'Quintozene (PCNB)', 'Resmethrin',
      'Simazine', 'Spinetoram', 'Spiromesifen', 'Sulfentrazone', 'Tebuconazole', 'Tebufenozide',
      'Tetrachlorvinphos', 'Tetradifon', 'Tetrahydrophthalimide (THPI)', 'Thiabendazole', 'Thiacloprid',
      'Thiamethoxam', 'Thiobencarb', 'Thiodicarb', 'Triadimefon', 'Triadimenol', 'Trifloxystrobin',
      'Triflumizole', 'Trifluralin', 'Vinclozolin'
    ]
  },
  {
    name: 'Phthalates',
    analytes: [
      'Benzylbutyl phthalate',
      'Bis (2-ethoxyethyl) phthalate',
      'Bis (2-ethylhexyl) phthalate',
      'Bis (2-methoxyethyl) phthalate',
      'Bis (2-n-butoxyethyl) phthalate',
      'Bis (4-methyl-2-pentyl) phthalate',
      'Di (2-ethylhexyl) terephthalate',
      'Diallyl phthalate',
      'Dibutyl phthalate',
      'Dicyclohexyl phthalate',
      'Diethylhexyl Adipate',
      'Diethyl phthalate',
      'Dihexyl phthalate',
      'Diisobutyl phthalate',
      'Diisodecyl Adipate',
      'Diisodecyl phthalate',
      'Diisononyl 1,2-cyclohexanedicarboxylic acid',
      'Diisononyl ortho phthalate',
      'Dimethyl phthalate',
      'Di-n-octylphthalate',
      'Dinonyl phthalate',
      'Dipentyl phthalate',
      'Diphenylphthalate'
    ]
  },
  {
    name: 'Mycotoxins',
    analytes: [
      'Aflatoxin B1', 'Aflatoxin B2', 'Aflatoxin G1', 'Aflatoxin G2', 'Deoxynivalenol',
      'Fumonisin B1', 'Fumonisin B3', 'Fusarium Toxin (T-2)', 'Mycotoxin HT-2', 'Ochratoxin A',
      'Total Aflatoxins (B1, B2, G1, G2)', 'Zearalenone'
    ]
  },
  {
    name: 'PFAS',
    analytes: []
  },
  {
    name: 'Yeast & Mold',
    analytes: ['Total Mold', 'Total Yeast']
  }
  // Hidden for now: 'Other' category (Linoleic Acid, etc.)
  // {
  //   name: 'Other',
  //   analytes: []
  // }
]
